import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import JSZip from 'jszip';
import {
  Upload,
  Trash2,
  Download,
  Settings,
  CheckCircle,
  Loader2,
  FileType
} from 'lucide-react';

interface FileToConvert {
  id: string;
  file: File;
  preview: string;
  status: 'pending' | 'processing' | 'completed' | 'error';
  progress: number;
  format: string;
  result?: string;
  resultName?: string;
}

const Converter: React.FC = () => {
  const [files, setFiles] = useState<FileToConvert[]>([]);
  const [targetFormat, setTargetFormat] = useState<'png' | 'jpeg' | 'webp' | 'ico'>('png');
  const [isProcessingAll, setIsProcessingAll] = useState(false);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newFiles = acceptedFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      preview: URL.createObjectURL(file),
      status: 'pending' as const,
      progress: 0,
      format: file.type.split('/')[1] || 'unknown',
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.webp', '.png', '.jpg', '.jpeg', '.gif', '.ico']
    }
  });

  const removeFile = (id: string) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((f) => f.id === id);
      if (fileToRemove) URL.revokeObjectURL(fileToRemove.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const convertFile = async (fileObj: FileToConvert, format: string): Promise<string | null> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;

        if (format === 'ico') {
          canvas.width = 32;
          canvas.height = 32;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }

        if (format === 'jpeg') {
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
        }

        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        const mimeType = format === 'ico' ? 'image/png' : `image/${format}`;
        const dataUrl = canvas.toDataURL(mimeType);
        resolve(dataUrl);
      };
      img.onerror = () => resolve(null);
      img.src = fileObj.preview;
    });
  };

  const getResultName = (originalName: string, format: string) => {
    const lastDotIndex = originalName.lastIndexOf('.');
    const basename = lastDotIndex !== -1 ? originalName.substring(0, lastDotIndex) : originalName;
    return `${basename}.${format}`;
  };

  const processAll = async () => {
    if (files.length === 0) return;
    setIsProcessingAll(true);

    const filesToProcess = [...files];

    for (const currentFile of filesToProcess) {
      if (currentFile.status === 'completed') continue;

      setFiles((prev) => prev.map((f) => f.id === currentFile.id ? { ...f, status: 'processing' } : f));

      const result = await convertFile(currentFile, targetFormat);

      if (result) {
        const resultName = getResultName(currentFile.file.name, targetFormat);
        setFiles((prev) => prev.map((f) =>
          f.id === currentFile.id ? {
            ...f,
            status: 'completed',
            result,
            resultName
          } : f
        ));
      } else {
        setFiles((prev) => prev.map((f) => f.id === currentFile.id ? { ...f, status: 'error' } : f));
      }
    }

    setIsProcessingAll(false);
  };

  const downloadSingle = (fileObj: FileToConvert) => {
    if (!fileObj.result || !fileObj.resultName) return;
    const link = document.createElement('a');
    link.href = fileObj.result;
    link.download = fileObj.resultName;
    link.click();
  };

  const downloadAll = async () => {
    const completedFiles = files.filter((f) => f.status === 'completed' && f.result && f.resultName);
    if (completedFiles.length === 0) return;

    if (completedFiles.length === 1) {
      downloadSingle(completedFiles[0]);
      return;
    }

    const zip = new JSZip();
    const usedNames = new Set<string>();

    completedFiles.forEach((f) => {
      let name = f.resultName!;
      const lastDotIndex = name.lastIndexOf('.');
      const base = lastDotIndex !== -1 ? name.substring(0, lastDotIndex) : name;
      const ext = lastDotIndex !== -1 ? name.substring(lastDotIndex + 1) : '';

      let finalName = name;
      let counter = 1;

      while (usedNames.has(finalName)) {
        finalName = `${base} (${counter})${ext ? '.' + ext : ''}`;
        counter++;
      }

      usedNames.add(finalName);

      const base64Data = f.result!.split(',')[1];
      zip.file(finalName, base64Data, { base64: true });
    });

    const content = await zip.generateAsync({ type: 'blob' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(content);
    link.href = url;
    link.download = 'bora_converter_pacote.zip';
    link.click();

    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  const clearFiles = () => {
    files.forEach((f) => URL.revokeObjectURL(f.preview));
    setFiles([]);
  };

  return (
    <div className="converter-container">
      <div className="options-bar">
        <label>
          <FileType className="icon" />
          Converter para:
        </label>
        <select
          value={targetFormat}
          onChange={(e) => setTargetFormat(e.target.value as any)}
          className="format-select"
        >
          <option value="png">PNG (Transparente)</option>
          <option value="jpeg">JPG (Compacto)</option>
          <option value="webp">WebP (Moderno)</option>
          <option value="ico">Favicon (32x32)</option>
        </select>

        {files.length > 0 && (
          <div className="batch-actions">
            <button
              onClick={processAll}
              disabled={isProcessingAll || files.every((f) => f.status === 'completed')}
              className="btn btn-primary"
            >
              {isProcessingAll ? (
                <><Loader2 className="animate-spin" /> Processando...</>
              ) : (
                <><Settings /> Converter tudo</>
              )}
            </button>
            <button
              onClick={downloadAll}
              disabled={!files.some((f) => f.status === 'completed')}
              className="btn btn-secondary"
            >
              <Download /> Baixar ZIP
            </button>
            <button
              onClick={clearFiles}
              disabled={isProcessingAll}
              className="btn btn-danger"
            >
              <Trash2 /> Limpar lista
            </button>
          </div>
        )}
      </div>

      <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
        <input {...getInputProps()} />
        <div className="dropzone-content">
          <Upload className="upload-icon" />
          <h3>Arraste e solte seu arquivo aqui</h3>
          <p>ou clique para selecionar</p>
          <span className="info">Suporta: WebP, PNG, JPG, ICO</span>
        </div>
      </div>

      {files.length > 0 && (
        <div className="file-list">
          {files.map((file) => (
            <div key={file.id} className="file-item">
              <div className="file-preview">
                <img src={file.preview} alt="Pré-visualização" />
              </div>
              <div className="file-info">
                <span className="file-name">{file.file.name}</span>
                <span className="file-meta">
                  {(file.file.size / 1024).toFixed(1)} KB · {file.format.toUpperCase()}
                </span>
                {file.status === 'completed' && <span className="status-badge success"><CheckCircle size={12} /> Pronto</span>}
                {file.status === 'processing' && <span className="status-badge processing"><Loader2 size={12} className="animate-spin" /> Convertendo</span>}
                {file.status === 'error' && <span className="status-badge error">Erro</span>}
              </div>
              <div className="file-actions">
                {file.status === 'completed' && (
                  <button onClick={() => downloadSingle(file)} className="action-btn download" title="Baixar">
                    <Download />
                  </button>
                )}
                <button onClick={() => removeFile(file.id)} className="action-btn remove" title="Remover">
                  <Trash2 />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Converter;